import React, {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import CardRelatedAd, {cardRelatedAdContactTypes, cardRelatedAdLeadTargets} from '@adv-mt/mt-card-related-ad'
import ListSlides from '@adv-mt/mt-list-slides'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {
  CARD_AD_TEST_ID,
  DEFAULT_MAX_ITEMS,
  defaultItemsPerRow,
  DISPLAY,
  ITEMS_PER_ROW,
  ITEMS_PER_ROW_WIDTH
} from './config.js'

function ListRelatedAds({
  ads = [],
  contactType = cardRelatedAdContactTypes.none,
  description,
  display = DISPLAY.grid,
  extendedTrackingData = {},
  hasClickableCards = false,
  hasCriteoEvents = false,
  hasFavouriteAction = false,
  itemsPerRow = defaultItemsPerRow,
  leadTarget = cardRelatedAdLeadTargets.relatedVehicle,
  link,
  maxItems = DEFAULT_MAX_ITEMS,
  numberOfSlides,
  onClickRelatedAd = () => {},
  onContactAd = () => {},
  title,
  titleNode,
  pageNameOrigin = '',
  carouselType = '',
  pageType = '',
  listType = ''
}) {
  const {domain} = useContext(Context)
  const {track} = useAnalyticsTrack()

  const hasAds = Boolean(ads.length)
  const hasLink = Boolean(link)
  const hasTitle = Boolean(title || titleNode)

  const config = domain.get('config')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  useMount(() => {
    if (!hasAds) return

    track({
      event: TRACKING_EVENT_NAMES.RECOMMENDATION_WIDGET_VIEWED,
      data: {
        carousel_type: carouselType,
        channel: 'ads',
        page_type: pageType,
        page_name_origin: pageNameOrigin
      }
    })
  })

  if (!hasAds) return <></>

  const listClassName = cx('mt-ListRelatedAds-list', `mt-ListRelatedAds-list--${display}`)
  const itemClassName =
    display === DISPLAY.slides
      ? cx('mt-ListRelatedAds-listItem--slides')
      : cx('mt-ListRelatedAds-listItem', `mt-ListRelatedAds-listItem--${ITEMS_PER_ROW_WIDTH[itemsPerRow]}`)

  const handleClickCard = ({ad, position}) => {
    onClickRelatedAd({ad, position})
  }
  const handleOnContact = ({ad, position}) => {
    onClickRelatedAd({ad, position})
    onContactAd(ad)
  }

  const cardItems = ads.slice(0, maxItems).map((ad, index) => (
    <div className={itemClassName} key={ad.id} data-testid={CARD_AD_TEST_ID}>
      <CardRelatedAd
        ad={ad}
        contactType={contactType}
        extendedTrackingData={extendedTrackingData}
        hasCriteoEvents={hasCriteoEvents}
        hasFavouriteAction={hasFavouriteAction}
        isClickableCard={hasClickableCards}
        leadTarget={leadTarget}
        onContact={ad => handleOnContact({ad, position: index})}
        onClickCard={() => handleClickCard({ad, position: index})}
        position={index}
        pageNameOrigin={pageNameOrigin}
        pageType={pageType}
        listType={listType}
      />
    </div>
  ))

  if (display === DISPLAY.slides) {
    return (
      <ListSlides
        items={cardItems}
        title={title}
        hasExternalArrows
        isFullWidth
        {...(numberOfSlides && {numberOfSlides})}
        {...(titleNode && {titleNode})}
      />
    )
  }

  return (
    <>
      {hasTitle && (
        <div className="mt-ListRelatedAds-head">
          <div className="mt-ListRelatedAds-headTitleWrapper">
            {titleNode || (
              <TitleBasic
                as={titleBasicTags.h2}
                size={titleBasicSizes.xs}
                title={title}
                description={description}
                descriptionColor={titleBasicColors.gray}
              />
            )}
          </div>
          {hasLink && link}
        </div>
      )}
      <div className={listClassName}>{cardItems}</div>
    </>
  )
}

ListRelatedAds.displayName = 'ListRelatedAds'
ListRelatedAds.propTypes = {
  ads: PropTypes.array.isRequired,
  contactType: PropTypes.oneOf(Object.values(cardRelatedAdContactTypes)),
  description: PropTypes.string,
  display: PropTypes.oneOf(Object.values(DISPLAY)),
  extendedTrackingData: PropTypes.object,
  hasClickableCards: PropTypes.bool,
  hasCriteoEvents: PropTypes.bool,
  hasFavouriteAction: PropTypes.bool,
  itemsPerRow: PropTypes.oneOf(Object.values(ITEMS_PER_ROW)),
  leadTarget: PropTypes.oneOf(Object.values(cardRelatedAdLeadTargets)),
  link: PropTypes.node,
  maxItems: PropTypes.number,
  numberOfSlides: PropTypes.number,
  onClickRelatedAd: PropTypes.func,
  onContactAd: PropTypes.func,
  title: PropTypes.string,
  titleNode: PropTypes.node,
  pageNameOrigin: PropTypes.string,
  carouselType: PropTypes.string,
  pageType: PropTypes.string,
  listType: PropTypes.string
}

export {
  cardRelatedAdContactTypes as listRelatedAdsContactTypes,
  cardRelatedAdLeadTargets as listRelatedAdsLeadTargets,
  DISPLAY as listRelatedAdsDisplay
}
export default ListRelatedAds
